import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createEvent,
  getEvents,
  getEventById,
  updateEvent,
  deleteEvent,
  getUpcomingEvents,
  getUpcomingEventsV2,
  getEventsV2,
  getClassEvents,
} from './eventApi';
import { toast } from 'react-toastify';

// Add retry configuration for network issues
const defaultRetryConfig = {
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

/**
 * Hook to fetch all events
 */
export const useGetEvents = () => {
  return useQuery({
    queryKey: ['events'],
    queryFn: getEvents,
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : 'Failed to fetch events: ' + (error.message || 'Unknown error');
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch all events using the version 2 endpoint
 */
export const useGetEventsV2 = (startDate, endDate, staffId) => {
  return useQuery({
    queryKey: ['events', startDate, endDate, staffId],
    queryFn: () => getEventsV2(startDate, endDate, staffId),
    keepPreviousData: true,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : 'Failed to fetch events: ' + (error.message || 'Unknown error');
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch all the class scheduled events (classes)
 */
export const useGetClassSchedule = (startDate, endDate, classId) => {
  return useQuery({
    queryKey: ['classEvents', startDate, endDate, classId],
    queryFn: () => getClassEvents(startDate, endDate, classId),
    keepPreviousData: true,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : 'Failed to fetch events: ' + (error.message || 'Unknown error');
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch all upcoming events
 */
export const useGetUpcomingEvents = () => {
  return useQuery({
    queryKey: ['upcomingEvents'],
    queryFn: getUpcomingEvents,
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : 'Failed to fetch events: ' + (error.message || 'Unknown error');
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch all upcoming events
 */
export const useGetUpcomingEventsV2 = (id) => {
  return useQuery({
    queryKey: ['upcomingEvents', id],
    queryFn: () => getUpcomingEventsV2(id),
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : 'Failed to fetch events: ' + (error.message || 'Unknown error');
      toast.error(message);
    },
  });
};

/**
 * Hook to fetch a single event by ID
 * @param {string} eventId - The ID of the event to fetch
 */
export const useGetEventById = (eventId) => {
  return useQuery({
    queryKey: ['events', eventId],
    queryFn: () => getEventById(eventId),
    enabled: !!eventId, // Only fetch when eventId is available
    ...defaultRetryConfig,
    onError: (error) => {
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : `Failed to fetch event: ${error.message || 'Unknown error'}`;
      toast.error(message);
    },
  });
};

/**
 * Hook to create a new event
 */
export const useCreateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    retry: 1,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['events']);
      toast.success('Event created successfully!');
      return data;
    },
    onError: (error) => {
      console.error('Error creating event:', error);
      const message =
        error.message === 'Network Error'
          ? 'Unable to connect to the server. Please check your internet connection.'
          : error.response?.data?.message || 'Failed to create event';
      toast.error(message);
    },
  });
};

/**
 * Hook to update an existing event
 */
export const useUpdateEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateEvent(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['events']);
      queryClient.invalidateQueries(['events', variables.id]);
      toast.success('Event updated successfully!');
      return data;
    },
    onError: (error) => {
      console.error('Error updating event:', error);
      toast.error(error.response?.data?.message || 'Failed to update event');
    },
  });
};

/**
 * Hook to delete an event
 */
export const useDeleteEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      toast.success('Event deleted successfully!');
    },
    onError: (error) => {
      console.error('Error deleting event:', error);
      toast.error(error.response?.data?.message || 'Failed to delete event');
    },
  });
};

/**
 * Combined hook for events management that focuses on single event operations
 * since getAllEvents endpoint is not yet available
 * @returns {Object} Event methods and data
 */
export const useEvents = () => {
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const deleteEventMutation = useDeleteEvent();

  // Format date for the calendar view
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Convert a single event to calendar-friendly format
  const formatEventForCalendar = (event) => {
    if (!event) return {};

    const formattedEvents = {};
    // Extract date from eventDate field which is in ISO format
    const eventDate = new Date(event.eventDate);
    const dateKey = formatDateKey(eventDate);

    if (!formattedEvents[dateKey]) {
      formattedEvents[dateKey] = [];
    }

    // Map API event format to calendar component format
    const formattedEvent = {
      id: event._id || event.id,
      title: event.eventTitle,
      description: event.description,
      date: dateKey,
      displayDate: eventDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      time: {
        start: new Date(event.startTime).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        end: new Date(event.endTime).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
      },
      location: event.location,
      type: event.eventType || 'other',
      color: getColorForEventType(event.eventType || 'other'),
      recurring: event.recurring,
      recurrenceRule: event.reccurenceRule,
      participants: event.participants || [],
    };

    formattedEvents[dateKey] = [formattedEvent];

    return formattedEvents;
  };

  // Map event type to color
  const getColorForEventType = (type) => {
    const typeColorMap = {
      academic: 'purple',
      meeting: 'blue',
      event: 'green',
      deadline: 'red',
      workshop: 'amber',
      other: 'gray',
    };

    return typeColorMap[type] || 'gray';
  };

  // In-memory cache of events we've already fetched
  const [cachedEvents, setCachedEvents] = React.useState({});
  const [eventsById, setEventsById] = React.useState({});

  // Since we don't have getAllEvents, we'll maintain our own collection
  const getAllEvents = () => {
    return Object.values(eventsById);
  };

  const getEventsByDate = (date) => {
    const dateKey = typeof date === 'string' ? date : formatDateKey(date);
    return cachedEvents[dateKey] || [];
  };

  const getEventsByType = (type) => {
    return getAllEvents().filter((event) => event.eventType === type);
  };

  const getEventsByDateRange = (startDate, endDate) => {
    return getAllEvents().filter((event) => {
      const eventDate = new Date(event.date);
      return eventDate >= startDate && eventDate <= endDate;
    });
  };

  const getUpcomingEvents = (limit = 5) => {
    const allEvents = getAllEvents();
    const now = new Date();
    return allEvents
      .filter((event) => new Date(event.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, limit);
  };

  // Fetch a single event by ID and add it to our cache
  const fetchEventById = async (eventId) => {
    if (!eventId) return null;

    try {
      const event = await getEventById(eventId);

      // Add to our in-memory collection
      setEventsById((prev) => ({
        ...prev,
        [eventId]: event,
      }));

      // Also update the date-based cache
      const formatted = formatEventForCalendar(event);
      setCachedEvents((prev) => ({
        ...prev,
        ...formatted,
      }));

      return event;
    } catch (error) {
      console.error(`Error fetching event ${eventId}:`, error);
      toast.error(`Failed to fetch event: ${error.message || 'Unknown error'}`);
      return null;
    }
  };

  const addEvent = async (eventData) => {
    // Format the data to match API expectations
    const apiEventData = {
      eventTitle: eventData.eventTitle,
      eventDate: eventData.eventDate,
      startTime: eventData.startTime,
      endTime: eventData.endTime,
      description: eventData.description,
      location: eventData.location,
      recurring: eventData.recurring,
      reccurenceRule: eventData.recurring ? eventData.reccurenceRule : null,
      eventType: eventData.eventType,
      participants: eventData.participants || [],
    };

    const newEvent = await createEventMutation.mutateAsync(apiEventData);

    // Add to our in-memory collection
    setEventsById((prev) => ({
      ...prev,
      [newEvent.id]: newEvent,
    }));

    // Also update the date-based cache
    const formatted = formatEventForCalendar(newEvent);
    setCachedEvents((prev) => ({
      ...prev,
      ...formatted,
    }));

    return newEvent;
  };

  const updateEventById = async (id, eventData) => {
    // Format the data to match API expectations
    const apiEventData = {
      eventTitle: eventData.eventTitle,
      eventDate: eventData.eventDate,
      startTime: eventData.startTime,
      endTime: eventData.endTime,
      description: eventData.description,
      location: eventData.location,
      recurring: eventData.recurring,
      reccurenceRule: eventData.recurring ? eventData.reccurenceRule : null,
      eventType: eventData.eventType,
      participants: eventData.participants || [],
    };

    const updatedEvent = await updateEventMutation.mutateAsync({
      id,
      data: apiEventData,
    });

    // Update in our in-memory collection
    setEventsById((prev) => ({
      ...prev,
      [id]: updatedEvent,
    }));

    // Also update the date-based cache
    const formatted = formatEventForCalendar(updatedEvent);
    setCachedEvents((prev) => ({
      ...prev,
      ...formatted,
    }));

    return updatedEvent;
  };

  const deleteEventById = async (id) => {
    await deleteEventMutation.mutateAsync(id);

    // Remove from our in-memory collection
    setEventsById((prev) => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });

    // Also remove from the date-based cache
    // This is more complex since we need to find which date it belongs to
    const event = eventsById[id];
    if (event && event.date) {
      setCachedEvents((prev) => {
        const newState = { ...prev };
        if (newState[event.date]) {
          newState[event.date] = newState[event.date].filter(
            (e) => e.id !== id
          );
          if (newState[event.date].length === 0) {
            delete newState[event.date];
          }
        }
        return newState;
      });
    }
  };

  return {
    events: cachedEvents,
    isLoading:
      createEventMutation.isLoading ||
      updateEventMutation.isLoading ||
      deleteEventMutation.isLoading,
    error:
      createEventMutation.error ||
      updateEventMutation.error ||
      deleteEventMutation.error,
    addEvent,
    updateEvent: updateEventById,
    deleteEvent: deleteEventById,
    getEventsByDate,
    getAllEvents,
    getUpcomingEvents,
    getEventsByType,
    getEventsByDateRange,
    fetchEventById,
  };
};
